import { toast } from 'sonner';

// 格式化数字（添加千位分隔符并限制小数位数）
export const formatNumber = (num: number, decimals: number = 2): string => {
  // 先限制小数位数，再添加千位分隔符
  const fixedNum = Number(num.toFixed(decimals));
  return fixedNum.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// 格式化日期
export const formatDate = (dateString: string | Date): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// 防抖函数
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// 节流函数
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// 统一的社区图像URL处理函数
// 确保社区图像正确显示，处理各种可能的字段名和URL格式
export const getCommunityAvatar = (community: any): string => {
  // 尝试多种可能的字段名
  const avatarUrl = community?.avatarUrl || 
                    community?.avatar || 
                    community?.logo || 
                    community?.image ||
                    community?.coverImage;
  
  // 如果找到了有效的URL
  if (avatarUrl && typeof avatarUrl === 'string' && avatarUrl.trim()) {
    const url = avatarUrl.trim();
    
    // 如果是完整的HTTP/HTTPS URL，直接返回
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // 如果是相对路径，尝试添加基础URL（如果需要）
    // 注意：这里假设相对路径已经是正确的，如果需要添加基础URL，可以在这里处理
    if (url.startsWith('/')) {
      // 如果是相对路径，可能需要添加API基础URL
      // 暂时直接返回，让浏览器处理
      return url;
    }
    
    // 其他情况，尝试作为完整URL返回
    return url;
  }
  
  // 如果没有找到有效的图片URL，返回默认图片
  // 使用社区用户默认图标（蓝色背景，DNA双螺旋和人物连接的抽象设计）
  return '/images/default-community-avatar.png';
};

// 生成唯一ID
export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// 错误处理函数
export const handleError = (error: Error, context?: string): void => {
  console.error(`[Error${context ? ` in ${context}` : ''}]:`, error);
  
  // 可以根据错误类型进行不同处理
  if (error.name === 'TypeError') {
    console.error('Type error detected. Check variable types and function parameters.');
  } else if (error.name === 'ReferenceError') {
    console.error('Reference error detected. Check if variables/functions are defined.');
  }
  
  // 显示错误通知
  toast.error(`操作失败: ${error.message}`);
};

// 初始化全局错误处理
export const initGlobalErrorHandlers = (): void => {
  // 捕获同步错误
  window.addEventListener('error', function(e) {
    const errorMessage = e.message;
    
    // 过滤浏览器扩展错误
    if (errorMessage.includes('message channel closed') || 
        errorMessage.includes('asynchronous response') ||
        errorMessage.includes('listener indicated')) {
      console.warn('🔇 忽略浏览器扩展错误:', errorMessage);
      e.preventDefault();
      return true;
    }
    
    // 过滤扩展文件错误
    if (e.filename && (e.filename.includes('extension') || 
        e.filename.startsWith('chrome-extension://') || 
        e.filename.startsWith('moz-extension://'))) {
      console.warn('🔇 忽略浏览器扩展文件错误:', e.filename);
      e.preventDefault();
      return true;
    }
    
    console.error('=== Global Error Capture ===');
    console.error('Error:', e.error);
    console.error('Filename:', e.filename);
    console.error('Line:', e.lineno);
    console.error('Column:', e.colno);
    console.error('Message:', e.message);
    console.error('Error Stack:', e.error?.stack);
    
    // 添加浏览器信息
    const browserInfo = getBrowserInfo();
    console.error('Browser:', `${browserInfo.name} ${browserInfo.version} (${browserInfo.os})`);
    console.error('============================');
    
    // 移除加载指示器
    const loadingIndicator = document.getElementById('app-loading-indicator');
    if (loadingIndicator) {
      loadingIndicator.style.display = 'none';
    }
    
    // 对于脚本加载错误，可以提供更具体的帮助信息
    if (e.target instanceof HTMLScriptElement) {
      toast.error(`脚本加载失败: ${e.filename}。请检查文件路径和网络连接。`);
      
      // 特别提示Windows用户检查路径
      if (browserInfo.os === 'Windows' && e.filename.includes('%')) {
        toast.error('注意：Windows系统下项目路径不能包含中文、空格或特殊字符！');
      }
    } else if (e.message.includes('Cannot find module') || e.message.includes('Failed to fetch dynamically imported module')) {
      toast.error(`模块加载失败: ${e.message.substring(0, 100)}... 请尝试重新安装依赖`);
    } else {
      toast.error(`应用发生错误: ${e.message.substring(0, 100)}...`);
    }
    
    // 在控制台提供详细的故障排除建议
    if (browserInfo.os === 'Windows') {
      console.log('\n===== 故障排除建议 =====');
      console.log('1. 确保项目路径不包含中文、空格或特殊字符');
      console.log('2. 尝试以管理员身份运行命令行');
      console.log('3. 执行以下命令清理缓存并重新安装依赖:');
      console.log('   npm cache clean --force');
      console.log('   pnpm store prune');
      console.log('   rm -rf node_modules pnpm-lock.yaml');
      console.log('   pnpm install');
      console.log('4. 参考项目中的LOCAL_DEV_GUIDE_WINDOWS.md文件');
      console.log('======================');
    }
    
    return false;
  });
  
  // 捕获未处理的Promise拒绝
  window.addEventListener('unhandledrejection', function(e) {
    const error = e.reason;
    const errorMessage = error && error.message ? error.message : String(error);
    
    // 过滤浏览器扩展相关的异步响应错误
    if (errorMessage.includes('message channel closed') || 
        errorMessage.includes('asynchronous response') ||
        errorMessage.includes('listener indicated')) {
      console.warn('🔇 忽略浏览器扩展异步响应错误:', errorMessage);
      e.preventDefault();
      return;
    }
    
    console.error('=== Unhandled Promise Rejection ===');
    console.error('Reason:', e.reason);
    console.error('Promise:', e.promise);
    console.error('Error Stack:', e.reason?.stack);
    console.error('============================');
    
    // 移除加载指示器
    const loadingIndicator = document.getElementById('app-loading-indicator');
    if (loadingIndicator) {
      loadingIndicator.style.display = 'none';
    }
    
    toast.error(`操作失败: ${e.reason?.message || '未知的Promise错误'}`);
  });
  
  // 监听页面加载状态变化
  const checkDocumentReady = () => {
    if (document.readyState === 'complete') {
      // 页面完全加载后检查是否仍有加载指示器
      setTimeout(() => {
        const loadingIndicator = document.getElementById('app-loading-indicator');
        const rootElement = document.getElementById('root');
        
        // 如果root有内容但加载指示器仍在显示，强制移除它
        if (loadingIndicator && rootElement && rootElement.innerHTML.trim() !== '') {
          loadingIndicator.style.opacity = '0';
          setTimeout(() => {
            loadingIndicator.remove();
          }, 500);
        } 
        // 如果root没有内容，强制显示一个简单的备用页面
        else if (loadingIndicator && rootElement && rootElement.innerHTML.trim() === '') {
          console.error('Root element is empty, showing fallback content');
          
          // 移除加载指示器
          loadingIndicator.style.opacity = '0';
          setTimeout(() => {
            loadingIndicator.remove();
          }, 500);
          
          // 创建一个简单的备用内容
          const fallbackContent = document.createElement('div');
          fallbackContent.id = 'app-fallback-content';
          fallbackContent.className = 'min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4';
          fallbackContent.innerHTML = `
            <div class="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6">
              <i class="fa-solid fa-globe text-blue-500 text-3xl"></i>
            </div>
            <h2 class="text-2xl font-bold text-gray-900 mb-2">欢迎访问指点地球村</h2>
            <p class="text-gray-600 mb-6 max-w-md text-center">
              应用正在加载中，请稍候...如果长时间无响应，请刷新页面重试
            </p>
            <div class="flex flex-col sm:flex-row gap-4">
              <button 
                id="refresh-button"
                class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <i class="fa-solid fa-rotate-right mr-2"></i>刷新页面
              </button>
              <button 
                id="goto-home-button"
                class="px-6 py-3 bg-white text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <i class="fa-solid fa-home mr-2"></i>返回首页
              </button>
            </div>
          `;
          
          // 添加到root元素
          rootElement.appendChild(fallbackContent);
          
          // 添加按钮事件
          setTimeout(() => {
            const refreshButton = document.getElementById('refresh-button');
            if (refreshButton) {
              refreshButton.addEventListener('click', () => window.location.reload());
            }
            
            const gotoHomeButton = document.getElementById('goto-home-button');
            if (gotoHomeButton) {
              gotoHomeButton.addEventListener('click', () => window.location.href = '/');
            }
          }, 100);
        }
      }, 1000); // 给应用1秒时间完成初始化
    }
  };
  
  // 增加一个专门的检测机制，确保页面内容在特定时间内渲染
  const checkPageContent = () => {
    setTimeout(() => {
      const rootElement = document.getElementById('root');
      const loadingIndicator = document.getElementById('app-loading-indicator');
      
      // 如果超过一定时间，root元素仍然为空，强制显示一个备用内容
      if (rootElement && rootElement.innerHTML.trim() === '' && loadingIndicator) {
        console.error('Page content not rendered within timeout, showing fallback');
        
        // 移除加载指示器
        loadingIndicator.style.opacity = '0';
        setTimeout(() => {
          loadingIndicator.remove();
        }, 500);
        
        // 创建一个简单的备用内容
        rootElement.innerHTML = `
          <div class="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
            <div class="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6">
              <i class="fa-solid fa-globe text-blue-500 text-3xl"></i>
            </div>
            <h2 class="text-2xl font-bold text-gray-900 mb-2">欢迎访问指点地球村</h2>
            <p class="text-gray-600 mb-6 max-w-md text-center">
              应用可能遇到了问题，请尝试刷新页面或检查网络连接
            </p>
            <div class="flex flex-col sm:flex-row gap-4">
              <button 
                onclick="window.location.reload()"
                class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <i class="fa-solid fa-rotate-right mr-2"></i>刷新页面
              </button>
              <button 
                onclick="window.location.href = '/'"
                class="px-6 py-3 bg-white text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <i class="fa-solid fa-home mr-2"></i>返回首页
              </button>
            </div>
          </div>
        `;
      }
    }, 10000); // 10秒后检查
  };
  
  // 监听DOMContentLoaded事件
  document.addEventListener('DOMContentLoaded', checkDocumentReady);
  // 监听window.load事件
  window.addEventListener('load', checkDocumentReady);
  
  // 启动页面内容检测
  checkPageContent();
  
  // 添加定期检查机制，确保加载指示器不会一直显示
  const intervalId = setInterval(() => {
    const loadingIndicator = document.getElementById('app-loading-indicator');
    const rootElement = document.getElementById('root');
    
    // 如果root有内容但加载指示器仍在显示，强制移除它
    if (loadingIndicator && rootElement && rootElement.innerHTML.trim() !== '') {
      loadingIndicator.style.opacity = '0';
      setTimeout(() => {
        loadingIndicator.remove();
      }, 500);
    }
  }, 2000); // 每2秒检查一次
  
  // 在页面卸载时清除定时器
  window.addEventListener('beforeunload', () => {
    clearInterval(intervalId);
  });
};

// 获取用户浏览器信息
export const getBrowserInfo = (): {
  name: string;
  version: string;
  os: string;
} => {
  const userAgent = navigator.userAgent;
  let name = 'Unknown';
  let version = 'Unknown';
  let os = 'Unknown';
  
  // 检测操作系统
  if (userAgent.includes('Windows')) os = 'Windows';
  else if (userAgent.includes('Macintosh')) os = 'Mac OS';
  else if (userAgent.includes('Linux')) os = 'Linux';
  else if (userAgent.includes('Android')) os = 'Android';
  else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) os = 'iOS';
  
  // 检测浏览器
  if (userAgent.includes('Chrome')) {
    name = 'Chrome';
    version = userAgent.match(/Chrome\/(\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Firefox')) {
    name = 'Firefox';
    version = userAgent.match(/Firefox\/(\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {name = 'Safari';
    version = userAgent.match(/Version\/(\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Edge')) {
    name = 'Edge';
    version = userAgent.match(/Edge\/(\d+)/)?.[1] || 'Unknown';
  }
  
  return { name, version, os };
};

// 身份证号码解析函数
export const parseIdCard = (idCard: string): { birthDate?: Date; gender?: 'male' | 'female'; region?: string } => {
  const result = {} as { birthDate?: Date; gender?: 'male' | 'female'; region?: string };
  
  // 仅支持18位中国大陆身份证
  if (idCard.length === 18) {
    try {
      // 提取出生日期 (第7-14位)
      const birthStr = idCard.substring(6, 14);
      const year = parseInt(birthStr.substring(0, 4));
      const month = parseInt(birthStr.substring(4, 6)) - 1; // 月份从0开始
      const day = parseInt(birthStr.substring(6, 8));
      result.birthDate = new Date(year, month, day);
      
      // 提取性别 (第17位，奇数为男，偶数为女)
      const genderCode = parseInt(idCard.substring(16, 17));
      result.gender = genderCode % 2 === 1 ? 'male' : 'female';
      
      // 提取地区码 (前6位)
      const regionCode = idCard.substring(0, 6);
      result.region = regionCode;
    } catch (error) {
      console.error('身份证解析失败:', error);
    }
  }
  
  return result;
};

// 计算年龄函数
export const calculateAge = (birthDate: Date): number => {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  // 如果生日还没过，年龄减1
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

// 验证是否成年
export const isAdult = (birthDate: Date): boolean => {
  return calculateAge(birthDate) >= 18;
};

// 证件类型配置
export const idTypeConfig = {
  idCard: {
    name: {
      zh: '身份证',
      en: 'ID Card',
      ar: 'بطاقة الهوية'
    },
    countries: ['CN', 'US', 'JP', 'GB', 'DE', 'FR', 'AU', 'CA']
  },
  passport: {
    name: {
      zh: '护照',
      en: 'Passport',
      ar: 'جواز سفر'
    },
    countries: ['CN', 'US', 'JP', 'GB', 'DE', 'FR', 'AU', 'CA', 'SG']
  },
  other: {
    name: {
      zh: '其他证件',
      en: 'Other ID',
      ar: '证件 أخرى'
    },
    countries: []
  }
};

// 获取证件类型名称
export const getIdTypeName = (idType: string, lang: string = 'zh') => {
  const typeConfig = idTypeConfig[idType as keyof typeof idTypeConfig];
  return typeConfig ? typeConfig.name[lang as keyof typeof typeConfig.name] || typeConfig.name.zh : idType;
};

// 获取证件样例图片URL
export const getIdSampleImage = (idType: string, countryCode: string, lang: string = 'zh') => {
  const typeNames = {
    idCard: {
      zh: '身份证',
      en: 'ID card',
      ar: 'بطاقة الهوية'
    },
    passport: {
      zh: '护照',
      en: 'Passport',
      ar: 'جواز سفر'
    }
  };
  
  const countryNames = {
    CN: {
      zh: '中国',
      en: 'China',
      ar: 'الصين'
    },
    US: {
      zh: '美国',
      en: 'USA',
      ar: 'الولايات المتحدة'
    },
    JP: {
      zh: '日本',
      en: 'Japan',
      ar: 'اليابان'
    },
    GB: {
      zh: '英国',
      en: 'UK',
      ar: 'المملكة المتحدة'
    },
    DE: {
      zh: '德国',
      en: 'Germany',
      ar: 'ألمانيا'
    },
    FR: {
      zh: '法国',
      en: 'France',
      ar: 'فرنسا'
    },
    AU: {
      zh: '澳大利亚',
      en: 'Australia',
      ar: 'أستراليا'
    },
    CA: {
      zh: '加拿大',
      en: 'Canada',
      ar: 'كندا'
    },
    SG: {
      zh: '新加坡',
      en: 'Singapore',
      ar: 'سنغافورة'
    }
  };
  
  const typeName = typeNames[idType as keyof typeof typeNames]?.[lang as keyof typeof typeNames.idCard] || idType;
  const countryName = countryNames[countryCode as keyof typeof countryNames]?.[lang as keyof typeof countryNames.CN] || countryCode;
  
  // 使用图片生成API创建证件样例图片
  const prompt = `${countryName} ${typeName} sample document`;
  return `https://space.coze.cn/api/coze_space/gen_image?image_size=landscape_4_3&prompt=${encodeURIComponent(prompt)}&sign=${Math.random().toString(36).substring(2, 10)}`;
};

// 存储工具
export const campaignStorage = {
  getCampaigns: (): any[] => {
    const campaigns = localStorage.getItem('campaigns');
    return campaigns ? JSON.parse(campaigns) : [];
  },
  
   saveCampaign: (campaign: any): any => {
    const campaigns = campaignStorage.getCampaigns();
    const existingIndex = campaigns.findIndex(c => c.id === campaign.id);
    
    // 如果是新创建的竞选，生成ID和初始状态
    if (!campaign.id) {
      campaign.id = campaignStorage.generateId();
      campaign.createdAt = campaign.createdAt || new Date().toISOString();
      campaign.voteCount = 0;
      campaign.status = campaign.status || 'pending_support'; // 默认为待支持状态
      campaign.requiredSupport = campaign.requiredSupport || 10;
      campaign.currentSupport = 0;
    }
    
    // 如果是视频内容，确保有审核状态
    if (campaign.videoType && !campaign.reviewStatus) {
      campaign.reviewStatus = campaign.reviewStatus || 'pending'; // 默认为待审核
    }
    
    // 优化存储：移除大型 data URL，只保留URL引用
    const optimizedCampaign = { ...campaign };
    
    // 如果 videoUrl 是 data URL 且过大（>1MB），移除它
    if (optimizedCampaign.videoUrl && optimizedCampaign.videoUrl.startsWith('data:video/')) {
      if (optimizedCampaign.videoUrl.length > 1024 * 1024) { // 1MB
        console.warn('[存储优化] 视频URL是过大的data URL，移除:', optimizedCampaign.id);
        optimizedCampaign.videoUrl = ''; // 清空过大的data URL
      }
    }
    
    // 如果 thumbnail 是 data URL 且过大（>500KB），移除它
    if (optimizedCampaign.thumbnail && optimizedCampaign.thumbnail.startsWith('data:video/')) {
      if (optimizedCampaign.thumbnail.length > 512 * 1024) { // 500KB
        console.warn('[存储优化] 缩略图是过大的data URL，移除:', optimizedCampaign.id);
        optimizedCampaign.thumbnail = optimizedCampaign.videoUrl || ''; // 使用视频URL作为缩略图
      }
    }
    
    // 清理旧数据：删除过期的或无效的视频（超过30天且URL无效）
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const cleanedCampaigns = campaigns.filter((c: any) => {
      // 保留有效的视频
      if (c.videoType && c.videoUrl && !c.videoUrl.includes('gen_image') && !c.videoUrl.includes('coze_space')) {
        return true;
      }
      // 保留30天内的数据
      if (c.createdAt) {
        const createdTime = new Date(c.createdAt).getTime();
        if (createdTime > thirtyDaysAgo) {
          return true;
        }
      }
      // 保留非视频类型的竞选
      if (!c.videoType) {
        return true;
      }
      // 删除无效的旧视频
      console.log('[存储清理] 删除无效的旧视频:', c.id);
      return false;
    });
    
    if (existingIndex >= 0) {
      cleanedCampaigns[existingIndex] = optimizedCampaign;
    } else {
      cleanedCampaigns.push(optimizedCampaign);
    }
    
    // 尝试保存，如果失败则清理更多数据
    try {
      const dataToSave = JSON.stringify(cleanedCampaigns);
      const dataSize = new Blob([dataToSave]).size;
      console.log('[存储] 准备保存数据，大小:', (dataSize / 1024 / 1024).toFixed(2), 'MB');
      
      // 如果数据过大（>4MB），清理更多旧数据
      if (dataSize > 4 * 1024 * 1024) {
        console.warn('[存储] 数据过大，清理更多旧数据');
        const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const moreCleanedCampaigns = cleanedCampaigns.filter((c: any) => {
          if (c.createdAt) {
            const createdTime = new Date(c.createdAt).getTime();
            return createdTime > sevenDaysAgo;
          }
          return true;
        });
        
        const moreCleanedData = JSON.stringify(moreCleanedCampaigns);
        const moreCleanedSize = new Blob([moreCleanedData]).size;
        console.log('[存储] 清理后数据大小:', (moreCleanedSize / 1024 / 1024).toFixed(2), 'MB');
        
        localStorage.setItem('campaigns', moreCleanedData);
        return optimizedCampaign;
      }
      
      localStorage.setItem('campaigns', dataToSave);
    } catch (error: any) {
      // 如果存储失败（配额超限），清理更多数据
      console.error('[存储] 保存失败，尝试清理更多数据:', error);
      
      // 只保留最近7天的数据
      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const emergencyCleanedCampaigns = cleanedCampaigns.filter((c: any) => {
        if (c.createdAt) {
          const createdTime = new Date(c.createdAt).getTime();
          return createdTime > sevenDaysAgo;
        }
        // 保留当前正在保存的campaign
        if (c.id === optimizedCampaign.id) {
          return true;
        }
        return false;
      });
      
      // 移除所有 data URL
      const finalCleanedCampaigns = emergencyCleanedCampaigns.map((c: any) => {
        const cleaned = { ...c };
        if (cleaned.videoUrl && cleaned.videoUrl.startsWith('data:')) {
          cleaned.videoUrl = '';
        }
        if (cleaned.thumbnail && cleaned.thumbnail.startsWith('data:')) {
          cleaned.thumbnail = '';
        }
        return cleaned;
      });
      
      try {
        localStorage.setItem('campaigns', JSON.stringify(finalCleanedCampaigns));
        console.log('[存储] 紧急清理后保存成功');
      } catch (finalError: any) {
        console.error('[存储] 紧急清理后仍然失败:', finalError);
        // 使用动态导入避免循环依赖
        import('sonner').then(({ toast }) => {
          toast.error('存储空间不足，请清理浏览器缓存或删除一些旧视频');
        });
        throw new Error('存储空间不足，无法保存视频');
      }
    }
    
    return optimizedCampaign;
  },
  
  deleteCampaign: (campaignId: string): void => {
    let campaigns = campaignStorage.getCampaigns();
    campaigns = campaigns.filter(c => c.id !== campaignId);
    try {
      localStorage.setItem('campaigns', JSON.stringify(campaigns));
    } catch (error: any) {
      console.error('[存储] 删除失败:', error);
      // 如果删除失败，尝试清理更多数据
      campaignStorage.cleanupOldData();
      // 重试删除
      campaigns = campaignStorage.getCampaigns();
      campaigns = campaigns.filter(c => c.id !== campaignId);
      localStorage.setItem('campaigns', JSON.stringify(campaigns));
    }
  },
  
  // 清理旧数据和无效数据
  cleanupOldData: (daysToKeep: number = 7): number => {
    const campaigns = campaignStorage.getCampaigns();
    const cutoffTime = Date.now() - daysToKeep * 24 * 60 * 60 * 1000;
    
    let cleanedCount = 0;
    const cleanedCampaigns = campaigns
      .filter((c: any) => {
        // 保留最近的数据
        if (c.createdAt) {
          const createdTime = new Date(c.createdAt).getTime();
          if (createdTime > cutoffTime) {
            return true;
          }
        }
        // 删除旧数据
        cleanedCount++;
        return false;
      })
      .map((c: any) => {
        // 移除大型 data URL
        const cleaned = { ...c };
        if (cleaned.videoUrl && cleaned.videoUrl.startsWith('data:') && cleaned.videoUrl.length > 1024 * 1024) {
          cleaned.videoUrl = '';
        }
        if (cleaned.thumbnail && cleaned.thumbnail.startsWith('data:') && cleaned.thumbnail.length > 512 * 1024) {
          cleaned.thumbnail = '';
        }
        return cleaned;
      });
    
    try {
      localStorage.setItem('campaigns', JSON.stringify(cleanedCampaigns));
      console.log(`[存储清理] 已清理 ${cleanedCount} 条旧数据`);
      return cleanedCount;
    } catch (error: any) {
      console.error('[存储清理] 清理失败:', error);
      // 如果仍然失败，只保留最近3天的数据
      const threeDaysAgo = Date.now() - 3 * 24 * 60 * 60 * 1000;
      const emergencyCleaned = cleanedCampaigns.filter((c: any) => {
        if (c.createdAt) {
          return new Date(c.createdAt).getTime() > threeDaysAgo;
        }
        return false;
      });
      localStorage.setItem('campaigns', JSON.stringify(emergencyCleaned));
      return cleanedCount;
    }
  },
  
  // 获取存储使用情况
  getStorageInfo: (): { size: number; count: number; sizeMB: string } => {
    try {
      const campaigns = campaignStorage.getCampaigns();
      const data = JSON.stringify(campaigns);
      const size = new Blob([data]).size;
      return {
        size,
        count: campaigns.length,
        sizeMB: (size / 1024 / 1024).toFixed(2)
      };
    } catch (error) {
      return { size: 0, count: 0, sizeMB: '0.00' };
    }
  },
  
  // 生成唯一ID
  generateId: (): string => {
    return 'camp_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
  },
  
   // 分享内容
  shareContent: (title: string, description: string, url: string): void => {
    toast.info(`分享功能：${title} - ${description} - ${url}`);
    // 模拟分享功能
    if (navigator.share) {
      navigator.share({ title, text: description, url })
        .catch(() => toast.error('分享失败，请手动复制链接分享'));
    } else {
      navigator.clipboard.writeText(url);
      toast.success('链接已复制到剪贴板');
    }
  },
  
  // 生成短链接
  generateShortLink: (username: string): string => {
    // 在实际应用中，这里应该调用API生成短链接
    // 这里使用模拟的短链接生成逻辑
    const hash = Array.from(username)
      .reduce((acc, char) => acc + char.charCodeAt(0), 0)
      .toString(36)
      .slice(-7);
    return `zd.li/${hash}`;
  },

  // 生成渠道专属链接
  generateChannelLink: (channelId: string, channelType: string): string => {
    // 在实际应用中，这里应该调用API生成专属链接
    // 这里使用模拟的链接生成逻辑
    const baseUrl = window.location.origin;
    // 为不同类型的渠道生成不同的链接参数
    const params = new URLSearchParams({
      cid: channelId,
      type: channelType,
      t: Date.now().toString() // 添加时间戳防止缓存
    });
    return `${baseUrl}/user/channel-signup/${channelId}?${params.toString()}`;
  },
  
  // 生成短链接函数已经在上面定义，此处删除重复定义
  // 投票功能
  voteForCampaign: (campaignId: string): { success: boolean; message: string } => {
    const campaigns = campaignStorage.getCampaigns();
    const campaignIndex = campaigns.findIndex(c => c.id === campaignId);
    
    // 检查用户是否已经投过票
    const userVotes = JSON.parse(localStorage.getItem('userVotes') || '{}');
    if (userVotes[campaignId]) {
      return { 
        success: false, 
        message: '您已经投过票了，无法再次投票' 
      };
    }
    
    if (campaignIndex >= 0) {
      // 检查竞选状态是否为进行中
      if (campaigns[campaignIndex].status !== 'active') {
        return { 
          success: false, 
          message: '该竞选尚未开始或已结束' 
        };
      }
      
      // 获取社区ID
      const communityId = campaigns[campaignIndex].communityId;
      
      // 检查用户是否已经在该社区投过票
      const communityVotes = JSON.parse(localStorage.getItem('communityVotes') || '{}');
      if (communityVotes[communityId]) {
        return {
          success: false,
          message: '您已经在该社区投过票了，无法再次投票给其他候选人'
        };
      }
      
      campaigns[campaignIndex].voteCount = (campaigns[campaignIndex].voteCount || 0) + 1;
      localStorage.setItem('campaigns', JSON.stringify(campaigns));
      
      // 记录用户投票
      userVotes[campaignId] = true;
      localStorage.setItem('userVotes', JSON.stringify(userVotes));
      
      // 记录社区投票，确保一个用户在一个社区只能投一次票
      communityVotes[communityId] = true;
      localStorage.setItem('communityVotes', JSON.stringify(communityVotes));
      
      return { 
        success: true, 
        message: '投票成功' 
      };
    }
    
    return { 
      success: false, 
      message: '找不到该竞选' 
    };
  },
  
  // 增加好友支持
  supportCampaign: (campaignId: string): { success: boolean; message: string } => {
    const campaigns = campaignStorage.getCampaigns();
    const campaignIndex = campaigns.findIndex(c => c.id === campaignId);
    
    // 检查用户是否已经支持过
    const userSupports = JSON.parse(localStorage.getItem('userSupports') || '{}');
    if (userSupports[campaignId]) {
      return { 
        success: false, 
        message: '您已经支持过该候选人了' 
      };
    }
    
    if (campaignIndex >= 0) {
      campaigns[campaignIndex].currentSupport = (campaigns[campaignIndex].currentSupport || 0) + 1;
      
      // 检查是否达到所需支持人数
      if (campaigns[campaignIndex].currentSupport >= campaigns[campaignIndex].requiredSupport) {
        campaigns[campaignIndex].status = 'active'; // 变为正式候选人
      }
      
      localStorage.setItem('campaigns', JSON.stringify(campaigns));
      
      // 记录用户支持
      userSupports[campaignId] = true;
      localStorage.setItem('userSupports', JSON.stringify(userSupports));
      
      return { 
        success: true, 
        message: '支持成功' 
      };
    }
    
    return { 
      success: false, 
      message: '找不到该竞选' 
    };
  },
  
  // 检查社区是否满足启动竞选条件
  checkElectionConditions: (community: any): boolean => {
    // 检查是否满足法定人数
    if (!community.minLegalMembers || community.memberCount < community.minLegalMembers) {
      return false;
    }
    
    // 如果已有领袖，检查任期是否结束
    if (community.hasLeader && community.leaderTermEnd) {
      const termEndDate = new Date(community.leaderTermEnd);
      const now = new Date();
      return now > termEndDate;
    }
    
    // 没有领袖且人数满足要求，可以启动竞选
    return !community.hasLeader;
  },
  
  // 计算社区领袖任期结束时间
  calculateTermEnd: (startDate: Date, termDays: number = 365): Date => {
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + termDays);
    return endDate;
  },
  
  // 检查社区领袖任期状态
  checkLeaderTermStatus: (termEnd?: string): { status: string; daysRemaining?: number } => {
    if (!termEnd) {
      return { status: 'no_leader' };
    }
    
    const termEndDate = new Date(termEnd);
    const now = new Date();
    const daysRemaining = Math.ceil((termEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining > 30) {
      return { status: 'normal', daysRemaining };
    } else if (daysRemaining > 0) {
      return { status: 'ending_soon', daysRemaining };
    } else {
      return { status: 'expired', daysRemaining: Math.abs(daysRemaining) };
    }
  }
};

// 生成分享海报
export const generateShareImage = async (
  backgroundImage: string,
  qrCodeUrl: string,
  title: string = "我正在竞选，支持我吧"
): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      // 创建canvas元素
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('无法创建canvas上下文'));
        return;
      }
      
      // 设置画布尺寸（竖屏尺寸）
      canvas.width = 750;
      canvas.height = 1334;
      
      // 绘制背景
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // 创建并加载背景图片
      const bgImg = new Image();
      bgImg.crossOrigin = 'anonymous';
      
      bgImg.onload = () => {
        try {
          // 计算图片位置和尺寸（保持宽高比）
          const maxWidth = canvas.width;
          const maxHeight = canvas.height;
          let width = bgImg.width;
          let height = bgImg.height;
          
          if (width > maxWidth) {
            const ratio = maxWidth / width;
            width = maxWidth;
            height = height * ratio;
          }
          
          if (height > maxHeight) {
            const ratio = maxHeight / height;
            height = maxHeight;
            width = width * ratio;
          }
          
          const x = (canvas.width - width) / 2;
          const y = (canvas.height - height) / 2;
          
          // 绘制背景图片
          ctx.drawImage(bgImg, x, y, width, height);
          
          // 添加半透明遮罩层，确保文字清晰可读
          ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
          ctx.fillRect(0, 0, canvas.width, 120);
          ctx.fillRect(0, canvas.height - 280, canvas.width, 280);
          
          // 绘制标题
          ctx.font = 'bold 36px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(title, canvas.width / 2, 60);
          
          // 加载二维码
          const qrCodeImg = new Image();
          qrCodeImg.crossOrigin = 'anonymous';
          
          qrCodeImg.onload = () => {
            try {
              // 绘制二维码（底部）
              const qrWidth = 200;
              const qrHeight = 200;
              const qrX = (canvas.width - qrWidth) / 2;
              const qrY = canvas.height - 220;
              
              // 绘制二维码白色背景
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(qrX - 15, qrY - 15, qrWidth + 30, qrHeight + 30);
              ctx.drawImage(qrCodeImg, qrX, qrY, qrWidth, qrHeight);
              
              // 添加二维码说明文字
              ctx.font = '18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
              ctx.fillStyle = '#333333';
              ctx.fillText('扫码加入', canvas.width / 2, qrY + qrHeight + 35);
              
              // 添加底部版权信息
              ctx.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
              ctx.fillStyle = '#666666';
              ctx.fillText('指点地球村 © 2025', canvas.width / 2, canvas.height - 20);
              
              // 转换为图片URL
              const imageUrl = canvas.toDataURL('image/png');
              resolve(imageUrl);
            } catch (error) {
              reject(error);
            }
          };
          
          qrCodeImg.onerror = () => {
            reject(new Error('加载二维码失败'));
          };
          
          qrCodeImg.src = qrCodeUrl;
        } catch (error) {
          reject(error);
        }
      };
      
      bgImg.onerror = () => {
        reject(new Error('加载背景图片失败'));
      };
      
      bgImg.src = backgroundImage;
    } catch (error) {
      reject(error);
    }
  });
};

// 下载图片
export const downloadImage = (imageUrl: string, filename: string = 'campaign-share.png') => {
  const link = document.createElement('a');
  link.href = imageUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// 模拟分享到微信
export const shareToWechat = (imageUrl: string, title: string, description: string) => {
  // 在实际环境中，这应该调用微信SDK的分享API
  // 这里我们只是模拟这个行为
  toast.info('已复制分享图片到剪贴板，请手动粘贴到微信好友对话框');
  
  // 尝试复制图片到剪贴板
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = async () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        try {
          // 尝试使用Clipboard API复制图片
          if (navigator.clipboard && window.ClipboardItem) {
            const blob = await new Promise<Blob>((resolve) => {
              canvas.toBlob(resolve, 'image/png');
            });
            
            if (blob) {
              const item = new ClipboardItem({ 'image/png': blob });
              await navigator.clipboard.write([item]);
              toast.success('分享图片已复制到剪贴板，请粘贴到微信好友对话框');
            }
          }
        } catch (clipboardError) {
          console.warn('复制图片到剪贴板失败:', clipboardError);
          // 如果失败，提供下载选项
          toast.info('请点击"下载图片"按钮，然后手动分享到微信');
        }
      }
    };
    img.src = imageUrl;
  } catch (error) {
    console.error('分享到微信失败:', error);
    toast.error('分享到微信失败，请先下载图片再手动分享');
  }
};

/** 微信内打开时的提示文案（分享海报用） */
export const WECHAT_OPEN_TIP = '微信内无法直接打开链接，请点击右上角「…」选择「在浏览器中打开」，或保存海报后扫码打开。';

/**
 * 用 URL 生成二维码 DataURL（前端生成，不依赖后端）
 */
export const generateQrCodeDataUrl = async (url: string, size: number = 280): Promise<string> => {
  const QRCode = await import('qrcode');
  const api = (QRCode as any).default || QRCode;
  return api.toDataURL(url, { width: size, margin: 1 });
};

/**
 * 生成「指点地球村」专属海报（品牌+标语+描述+动态二维码），用户扫码注册后可看分享用户所在社区详情
 */
export const generateExclusivePoster = async (options: {
  qrCodeImageUrl: string;  // 二维码图片 URL 或 DataURL（对应分享链接，含 referrerId、communityId）
  title?: string;          // 主标题，默认「指点地球村」
  slogan?: string;         // 副标题/标语
  description?: string;   // 描述文案
}): Promise<string> => {
  const title = options.title ?? '指点地球村';
  const slogan = options.slogan ?? '你想亲手打造一个属于你的社区吗？';
  const description = options.description ?? '助力用户共建、共创的线上共享经济社区，社区机制透明公开，人人有机会做社区领袖';

  const canvas = document.createElement('canvas');
  const width = 750;
  const height = 1334;
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法创建 canvas 上下文');

  // 背景
  ctx.fillStyle = '#f8fafc';
  ctx.fillRect(0, 0, width, height);

  // 顶部渐变条
  const gradient = ctx.createLinearGradient(0, 0, 0, 200);
  gradient.addColorStop(0, '#2563eb');
  gradient.addColorStop(1, '#1d4ed8');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, 200);

  // 主标题（蓝色 3D 感）
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 42px -apple-system, "PingFang SC", "Microsoft YaHei", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(title, width / 2, 80);

  // 标语
  ctx.font = '20px -apple-system, "PingFang SC", sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.fillText(slogan, width / 2, 140);

  // 描述区域（白底卡片）
  const cardY = 240;
  const cardH = 320;
  ctx.fillStyle = '#ffffff';
  ctx.shadowColor = 'rgba(0,0,0,0.08)';
  ctx.shadowBlur = 12;
  ctx.shadowOffsetY = 4;
  ctx.fillRect(40, cardY, width - 80, cardH);
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  ctx.fillStyle = '#334155';
  ctx.font = '16px -apple-system, "PingFang SC", sans-serif';
  ctx.textAlign = 'left';
  const lineHeight = 26;
  const maxLineWidth = width - 120;
  const words = description.split('');
  let line = '';
  let y = cardY + 36;
  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i];
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxLineWidth && line.length > 0) {
      ctx.fillText(line, 60, y);
      line = words[i];
      y += lineHeight;
    } else {
      line = testLine;
    }
  }
  if (line) ctx.fillText(line, 60, y);

  // 二维码区域（底部居中）
  const qrSize = 280;
  const qrX = (width - qrSize) / 2;
  const qrY = height - 380;
  const qrImg = new Image();
  qrImg.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    qrImg.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(qrX - 16, qrY - 16, qrSize + 32, qrSize + 32);
      ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
      resolve();
    };
    qrImg.onerror = () => reject(new Error('二维码图片加载失败'));
    qrImg.src = options.qrCodeImageUrl;
  });

  ctx.fillStyle = '#64748b';
  ctx.font = '14px -apple-system, "PingFang SC", sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('扫码注册，查看分享者所在社区详情', width / 2, qrY + qrSize + 40);
  ctx.font = '12px -apple-system, "PingFang SC", sans-serif';
  ctx.fillStyle = '#94a3b8';
  ctx.fillText('微信内：长按识别 → 点右上角 … → 打开浏览器', width / 2, qrY + qrSize + 62);
  ctx.fillStyle = '#64748b';
  ctx.font = '14px -apple-system, "PingFang SC", sans-serif';
  ctx.fillText('指点地球村 © 2025', width / 2, height - 24);

  return canvas.toDataURL('image/png');
};

/** 分享到 QQ：复制图片 + 提示 */
export const shareToQQ = (imageUrl: string, shareUrl: string, title: string) => {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = async () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
        if (blob && navigator.clipboard && window.ClipboardItem) {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
          toast.success('海报已复制，请粘贴到 QQ 好友或 QQ 空间');
        } else {
          downloadImage(imageUrl, `指点地球村-${title}.png`);
          toast.info('请保存图片后到 QQ 中发送');
        }
      }
    };
    img.src = imageUrl;
  } catch {
    downloadImage(imageUrl, `指点地球村-${title}.png`);
    toast.info('请保存图片后到 QQ 中分享');
  }
};

/** 分享到小红书：复制链接 + 提示保存图片 */
export const shareToXiaohongshu = (imageUrl: string, shareUrl: string, title: string) => {
  navigator.clipboard.writeText(shareUrl).then(() => {
    toast.success('链接已复制，请保存海报图片后到小红书发布');
  });
  downloadImage(imageUrl, `指点地球村-${title}.png`);
};

/** 分享到抖音：复制链接 + 下载海报，提示到抖音中发送 */
export const shareToDouyin = (imageUrl: string, shareUrl: string, title: string) => {
  navigator.clipboard.writeText(shareUrl).then(() => {
    toast.success('链接已复制，请保存海报后到抖音中发送给好友');
  });
  downloadImage(imageUrl, `指点地球村-${title}.png`);
};

/** 分享到电话通讯录/系统分享：保存图片 + 复制链接 */
export const shareToPhoneContacts = (imageUrl: string, shareUrl: string, title: string) => {
  const text = `${title}\n${shareUrl}`;
  navigator.clipboard.writeText(text).then(() => {
    toast.success('链接已复制到剪贴板，可粘贴到短信或通讯录备注');
  });
  downloadImage(imageUrl, `指点地球村-${title}.png`);
};

// 生成简历PDF（真实实现）
export const generateResumePDF = async (resumeData: any): Promise<Blob> => {
  try {
    // 动态导入jsPDF和html2canvas
    const { default: jsPDF } = await import('jspdf');
    const html2canvas = (await import('html2canvas')).default;
    
    // 创建临时容器用于渲染简历内容
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    // A4宽度210mm，减去左右边距30mm（各15mm），内容宽度为180mm
    tempContainer.style.width = '180mm'; // 内容宽度（已减去边距）
    tempContainer.style.padding = '15mm'; // 内边距
    tempContainer.style.backgroundColor = '#ffffff';
    tempContainer.style.fontFamily = 'Arial, "Microsoft YaHei", sans-serif';
    tempContainer.style.fontSize = '12px';
    tempContainer.style.lineHeight = '1.6';
    tempContainer.style.color = '#333333';
    tempContainer.style.boxSizing = 'border-box';
    
    // 构建简历HTML内容
    // 为每个块级元素添加page-break-inside: avoid，避免在元素中间分页
    let htmlContent = `
      <div style="max-width: 100%;">
        <!-- 个人信息 -->
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #2563eb; padding-bottom: 20px; page-break-inside: avoid; break-inside: avoid;">
          <h1 style="margin: 0 0 10px 0; font-size: 28px; color: #1e40af; font-weight: bold;">
            ${resumeData.userInfo?.name || '姓名'}
          </h1>
          <div style="display: flex; justify-content: center; gap: 20px; font-size: 14px; color: #666; flex-wrap: wrap;">
            ${resumeData.userInfo?.phone ? `<span>手机：${resumeData.userInfo.phone}</span>` : ''}
            ${resumeData.userInfo?.email ? `<span>邮箱：${resumeData.userInfo.email}</span>` : ''}
            ${resumeData.userInfo?.city ? `<span>地址：${resumeData.userInfo.city}</span>` : ''}
          </div>
        </div>
        
        <!-- 求职意向 -->
        ${resumeData.jobIntention ? `
        <div style="margin-bottom: 25px; page-break-inside: avoid; break-inside: avoid;">
          <h2 style="font-size: 18px; color: #1e40af; border-left: 4px solid #2563eb; padding-left: 10px; margin-bottom: 15px;">
            求职意向
          </h2>
          <div style="padding-left: 14px;">
            ${resumeData.jobIntention.jobTitle ? `<p style="margin: 5px 0; page-break-inside: avoid; break-inside: avoid;">期望岗位：<strong>${resumeData.jobIntention.jobTitle}</strong></p>` : ''}
            ${resumeData.jobIntention.location ? `<p style="margin: 5px 0; page-break-inside: avoid; break-inside: avoid;">期望地点：<strong>${resumeData.jobIntention.location}</strong></p>` : ''}
            ${resumeData.jobIntention.salary ? `<p style="margin: 5px 0; page-break-inside: avoid; break-inside: avoid;">期望薪资：<strong>${resumeData.jobIntention.salary}</strong></p>` : ''}
            ${resumeData.jobIntention.notes ? `<p style="margin: 5px 0; page-break-inside: avoid; break-inside: avoid;">备注：${resumeData.jobIntention.notes}</p>` : ''}
          </div>
        </div>
        ` : ''}
        
        <!-- 工作履历 -->
        ${resumeData.workExperiences && resumeData.workExperiences.length > 0 ? `
        <div style="margin-bottom: 25px;">
          <h2 style="font-size: 18px; color: #1e40af; border-left: 4px solid #2563eb; padding-left: 10px; margin-bottom: 15px;">
            工作履历
          </h2>
          ${resumeData.workExperiences.map((exp: any, index: number) => `
            <div style="margin-bottom: 20px; padding-left: 14px; page-break-inside: avoid; break-inside: avoid;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                <h3 style="margin: 0; font-size: 16px; font-weight: bold; color: #1e40af;">
                  ${exp.position || '职位'} | ${exp.company || '公司名称'}
                </h3>
                <span style="font-size: 12px; color: #666;">
                  ${exp.startDate || ''} - ${exp.endDate || ''}
                </span>
              </div>
              ${exp.description ? `<p style="margin: 8px 0; color: #555; line-height: 1.8; page-break-inside: avoid; break-inside: avoid;">${exp.description}</p>` : ''}
              ${exp.achievements && exp.achievements.length > 0 ? `
                <div style="margin-top: 10px; page-break-inside: avoid; break-inside: avoid;">
                  <strong style="color: #1e40af;">主要成就：</strong>
                  <ul style="margin: 5px 0; padding-left: 20px; color: #555;">
                    ${exp.achievements.filter((a: string) => a && a.trim()).map((achievement: string) => `<li style="margin: 5px 0; page-break-inside: avoid; break-inside: avoid;">${achievement}</li>`).join('')}
                  </ul>
                </div>
              ` : ''}
            </div>
          `).join('')}
        </div>
        ` : ''}
        
        <!-- 技能信息 -->
        ${resumeData.skills && resumeData.skills.length > 0 ? `
        <div style="margin-bottom: 25px;">
          <h2 style="font-size: 18px; color: #1e40af; border-left: 4px solid #2563eb; padding-left: 10px; margin-bottom: 15px;">
            技能信息
          </h2>
          <div style="padding-left: 14px;">
            ${resumeData.skills.map((skill: any) => `
              <div style="margin-bottom: 15px; page-break-inside: avoid; break-inside: avoid;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px;">
                  <strong style="font-size: 14px; color: #1e40af;">${skill.name || '技能名称'}</strong>
                  <span style="font-size: 12px; color: #666;">熟练度: ${'★'.repeat(skill.level || 0)}${'☆'.repeat(5 - (skill.level || 0))}</span>
                </div>
                ${skill.description ? `<p style="margin: 5px 0; color: #555; font-size: 12px; page-break-inside: avoid; break-inside: avoid;">${skill.description}</p>` : ''}
              </div>
            `).join('')}
          </div>
        </div>
        ` : ''}
        
        <!-- 荣誉证书 -->
        ${resumeData.certificates && resumeData.certificates.length > 0 ? `
        <div style="margin-bottom: 25px;">
          <h2 style="font-size: 18px; color: #1e40af; border-left: 4px solid #2563eb; padding-left: 10px; margin-bottom: 15px;">
            荣誉证书
          </h2>
          <div style="padding-left: 14px;">
            ${resumeData.certificates.map((cert: any) => `
              <div style="margin-bottom: 15px; page-break-inside: avoid; break-inside: avoid;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px;">
                  <strong style="font-size: 14px; color: #1e40af;">${cert.title || '证书名称'}</strong>
                  ${cert.issueDate ? `<span style="font-size: 12px; color: #666;">${cert.issueDate}</span>` : ''}
                </div>
                ${cert.issuer ? `<p style="margin: 5px 0; color: #666; font-size: 12px; page-break-inside: avoid; break-inside: avoid;">发证机构：${cert.issuer}</p>` : ''}
                ${cert.description ? `<p style="margin: 5px 0; color: #555; font-size: 12px; page-break-inside: avoid; break-inside: avoid;">${cert.description}</p>` : ''}
              </div>
            `).join('')}
          </div>
        </div>
        ` : ''}
      </div>
    `;
    
    tempContainer.innerHTML = htmlContent;
    document.body.appendChild(tempContainer);
    
    // 等待DOM渲染完成
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // 使用html2canvas将HTML转换为Canvas
    const canvas = await html2canvas(tempContainer, {
      scale: 2, // 提高清晰度
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: tempContainer.scrollWidth,
      height: tempContainer.scrollHeight,
      windowWidth: tempContainer.scrollWidth,
      windowHeight: tempContainer.scrollHeight
    });
    
    // 清理临时容器
    document.body.removeChild(tempContainer);
    
    // 计算PDF尺寸（A4: 210mm x 297mm）
    // PDF边距：左右各15mm，上下各15mm
    const pdfMargin = 15; // 边距（mm）
    const pdfWidth = 210; // A4宽度（mm）
    const pdfHeight = 297; // A4高度（mm）
    const contentWidth = pdfWidth - (pdfMargin * 2); // 内容宽度（180mm）
    const contentHeight = pdfHeight - (pdfMargin * 2); // 内容高度（267mm）
    
    // 计算图片在PDF中的尺寸（保持宽高比）
    const imgWidth = contentWidth; // 使用内容宽度
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imageData = canvas.toDataURL('image/png', 0.95);
    
    // 如果内容超过一页，需要分页
    if (imgHeight <= contentHeight) {
      // 内容在一页内，直接添加
      pdf.addImage(imageData, 'PNG', pdfMargin, pdfMargin, imgWidth, imgHeight);
    } else {
      // 内容超过一页，需要分页
      // 使用智能分页算法，确保文字行完整且不超出边界
      // 估算行高（基于字体大小和行高），用于智能分页
      const estimatedLineHeight = 12 * 1.6 * 2; // 12px字体 * 1.6行高 * 2倍scale，转换为像素
      const lineHeightMM = estimatedLineHeight / (canvas.height / imgHeight); // 转换为毫米
      
      let totalHeight = imgHeight;
      let yPosition = pdfMargin; // 当前页的Y位置（PDF中的毫米）
      let sourceY = 0; // Canvas中的源Y位置（像素）
      let pageNumber = 0;
      
      while (totalHeight > 0) {
        if (pageNumber > 0) {
          pdf.addPage();
          yPosition = pdfMargin; // 新页从边距位置开始
        }
        
        // 计算这一页可以容纳的高度（毫米）
        const availableHeight = contentHeight;
        
        // 计算当前页可以显示的最大高度
        // 确保不超过可用高度，避免超出边界
        let maxPageHeight = Math.min(totalHeight, availableHeight);
        
        // 智能分页：如果剩余内容接近页面底部（小于2行高度），将整块内容移到下一页
        if (totalHeight > availableHeight && totalHeight - availableHeight < lineHeightMM * 2) {
          // 如果下一页能容纳剩余内容，就移到下一页
          if (totalHeight <= availableHeight * 2) {
            // 移到下一页
            if (pageNumber === 0) {
              pdf.addPage();
              yPosition = pdfMargin;
            }
            maxPageHeight = totalHeight;
          }
        }
        
        // 计算Canvas中对应的源高度（像素）
        const sourceHeightPixels = (maxPageHeight / imgHeight) * canvas.height;
        
        // 创建临时canvas来裁剪当前页的内容
        // 向上取整到最近的整行，确保文字行完整
        let pageCanvasHeight = Math.ceil(sourceHeightPixels / estimatedLineHeight) * estimatedLineHeight;
        
        // 确保不超出canvas边界
        const maxCanvasHeight = canvas.height - Math.floor(sourceY);
        pageCanvasHeight = Math.min(pageCanvasHeight, maxCanvasHeight);
        
        // 计算对应的PDF高度
        const pdfHeightForCanvas = (pageCanvasHeight / canvas.height) * imgHeight;
        
        // 确保PDF高度不超过可用高度
        if (pdfHeightForCanvas > availableHeight) {
          // 调整canvas高度，确保PDF高度不超过可用高度
          const adjustedCanvasHeight = (availableHeight / imgHeight) * canvas.height;
          pageCanvasHeight = Math.floor(adjustedCanvasHeight / estimatedLineHeight) * estimatedLineHeight;
          // 确保不超出边界
          pageCanvasHeight = Math.min(pageCanvasHeight, maxCanvasHeight);
        }
        
        // 再次检查：确保添加到PDF后不会超出页面底部边界
        const finalPdfHeight = (pageCanvasHeight / canvas.height) * imgHeight;
        const maxAllowedY = pdfHeight - pdfMargin;
        
        // 检查是否会超出页面底部边界
        if (yPosition + finalPdfHeight > maxAllowedY) {
          // 如果会超出，计算可以放入当前页的最大高度
          const maxHeightForCurrentPage = maxAllowedY - yPosition;
          
          if (maxHeightForCurrentPage >= lineHeightMM) {
            // 当前页还能放下至少一行，调整canvas高度
            const adjustedCanvasHeightForBoundary = (maxHeightForCurrentPage / imgHeight) * canvas.height;
            pageCanvasHeight = Math.floor(adjustedCanvasHeightForBoundary / estimatedLineHeight) * estimatedLineHeight;
            pageCanvasHeight = Math.min(pageCanvasHeight, maxCanvasHeight);
          } else {
            // 当前页放不下，移到下一页
            pdf.addPage();
            yPosition = pdfMargin;
            // pageCanvasHeight保持不变，因为已经计算好了
          }
        }
        
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = pageCanvasHeight;
        const pageCtx = pageCanvas.getContext('2d');
        
        if (pageCtx) {
          // 设置白色背景，避免透明区域
          pageCtx.fillStyle = '#ffffff';
          pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          
          // 从原canvas中复制当前页的内容
          const sourceYInt = Math.floor(sourceY);
          const sourceHeightInt = Math.min(pageCanvasHeight, canvas.height - sourceYInt);
          
          pageCtx.drawImage(
            canvas,
            0, sourceYInt, // 源位置（像素，向下取整）
            canvas.width, sourceHeightInt, // 源尺寸（像素）
            0, 0, // 目标位置
            pageCanvas.width, pageCanvas.height // 目标尺寸
          );
          
          // 将裁剪后的canvas添加到PDF
          const pageImageData = pageCanvas.toDataURL('image/png', 0.95);
          
          // 计算实际显示高度（毫米）
          const actualPageHeight = (pageCanvas.height / canvas.height) * imgHeight;
          
          // 确保添加到PDF的高度不超过可用高度，避免超出边界
          let finalPageHeight = Math.min(actualPageHeight, availableHeight);
          
          // 最终检查：确保不会超出页面底部边界
          const finalMaxAllowedY = pdfHeight - pdfMargin;
          if (yPosition + finalPageHeight > finalMaxAllowedY) {
            // 如果仍然会超出，调整高度
            finalPageHeight = finalMaxAllowedY - yPosition;
            
            // 如果调整后的高度太小（小于一行），将内容移到下一页
            if (finalPageHeight < lineHeightMM) {
              pdf.addPage();
              yPosition = pdfMargin;
              // 重新计算这一页的高度
              finalPageHeight = Math.min(actualPageHeight, availableHeight);
            }
          }
          
          // 确保最终高度不为负或零
          if (finalPageHeight > 0) {
            pdf.addImage(
              pageImageData, 
              'PNG', 
              pdfMargin, 
              yPosition, 
              contentWidth, 
              finalPageHeight // 使用调整后的高度，确保不超出边界
            );
          }
        }
        
        // 更新位置
        // 使用实际裁剪的像素高度来计算下一页的起始位置
        const actualSourceHeight = pageCanvas.height;
        sourceY += actualSourceHeight;
        
        // 更新剩余高度（使用实际显示的高度）
        const actualDisplayHeight = (actualSourceHeight / canvas.height) * imgHeight;
        totalHeight -= actualDisplayHeight;
        pageNumber++;
        
        // 防止无限循环
        if (pageNumber > 100) {
          console.error('PDF分页超过100页，可能存在错误');
          break;
        }
      }
    }
    
    // 返回PDF Blob
    const pdfBlob = pdf.output('blob');
    return pdfBlob;
  } catch (error) {
    console.error('生成PDF失败:', error);
    throw new Error('生成PDF失败，请稍后重试');
  }
};

// 分享简历到不同平台
export const shareResume = (platform: string): void => {
  // 获取简历链接（在实际应用中，这应该是一个真实的简历页面链接）
  const resumeUrl = window.location.origin + '/user/settings/resume';
  
  switch (platform) {
    case 'wechat':
      // 模拟微信分享
      toast.info('已复制简历链接到剪贴板，请在微信中粘贴分享');
      navigator.clipboard.writeText(resumeUrl);
      break;
    case 'douyin':
      // 模拟抖音分享
      toast.info('已复制简历链接到剪贴板，请在抖音中粘贴分享');
      navigator.clipboard.writeText(resumeUrl);
      break;
    case 'xiaohongshu':
      // 模拟小红书分享
      toast.info('已复制简历链接到剪贴板，请在小红书笔记中粘贴分享');
      navigator.clipboard.writeText(resumeUrl);
      break;
    default:
      // 其他平台分享
      toast.info(`已复制简历链接到剪贴板，请在${platform}中粘贴分享`);
      navigator.clipboard.writeText(resumeUrl);
  }
};

// 社区历史数据管理工具函数
export const communityHistoryUtils = {
  // 从localStorage获取历史社区ID列表
  getHistoryCommunityIds: (): string[] => {
    try {
      const savedCommunities = localStorage.getItem('communityListHistory');
      if (savedCommunities) {
        const parsed = JSON.parse(savedCommunities);
        if (Array.isArray(parsed)) {
          return parsed.map((c: any) => c.id || c.communityId).filter(Boolean);
        }
      }
    } catch (error) {
      console.warn('[communityHistoryUtils] 获取历史社区ID失败:', error);
    }
    return [];
  },

  // 从localStorage移除指定的社区ID
  removeHistoryCommunityId: (communityId: string): void => {
    try {
      const savedCommunities = localStorage.getItem('communityListHistory');
      if (savedCommunities) {
        const parsed = JSON.parse(savedCommunities);
        if (Array.isArray(parsed)) {
          const filtered = parsed.filter((c: any) => {
            const id = c.id || c.communityId;
            return id !== communityId;
          });
          localStorage.setItem('communityListHistory', JSON.stringify(filtered));
          console.log(`[communityHistoryUtils] 已从历史记录中移除社区 ${communityId}`);
        }
      }
    } catch (error) {
      console.warn('[communityHistoryUtils] 移除历史社区ID失败:', error);
    }
  },

  // 验证历史数据中的社区ID是否仍然有效，移除无效的社区ID
  validateAndCleanHistoryCommunities: async (): Promise<string[]> => {
    const { communityAPI } = await import('@/lib/api');
    const historyIds = communityHistoryUtils.getHistoryCommunityIds();
    const validIds: string[] = [];
    const invalidIds: string[] = [];

    // 批量验证社区ID（限制并发数，避免过多请求）
    const batchSize = 5;
    for (let i = 0; i < historyIds.length; i += batchSize) {
      const batch = historyIds.slice(i, i + batchSize);
      await Promise.all(
        batch.map(async (communityId) => {
          try {
            const response = await communityAPI.getCommunityDetail(communityId);
            if (response.code === 200 && response.data) {
              validIds.push(communityId);
            } else if (response.code === 404 || response.status === 404) {
              // 社区不存在，标记为无效
              invalidIds.push(communityId);
              console.log(`[communityHistoryUtils] 社区 ${communityId} 不存在，将从历史记录中移除`);
            } else {
              // 其他错误，暂时保留（可能是网络问题）
              validIds.push(communityId);
            }
          } catch (error: any) {
            // 404错误表示社区不存在
            if (error.status === 404 || error.message?.includes('404') || error.message?.includes('Not Found')) {
              invalidIds.push(communityId);
              console.log(`[communityHistoryUtils] 社区 ${communityId} 不存在，将从历史记录中移除`);
            } else {
              // 其他错误，暂时保留
              validIds.push(communityId);
            }
          }
        })
      );
    }

    // 移除无效的社区ID
    if (invalidIds.length > 0) {
      invalidIds.forEach((id) => {
        communityHistoryUtils.removeHistoryCommunityId(id);
      });
      console.log(`[communityHistoryUtils] 已清理 ${invalidIds.length} 个无效的社区ID`);
    }

    return validIds;
  },

  // 批量验证社区ID是否在API返回的社区列表中（更高效的方法）
  validateHistoryCommunitiesAgainstAPI: async (apiCommunities: any[]): Promise<string[]> => {
    const historyIds = communityHistoryUtils.getHistoryCommunityIds();
    const apiCommunityIds = new Set(
      apiCommunities.map((c: any) => c.id || c.communityId).filter(Boolean)
    );

    const validIds: string[] = [];
    const invalidIds: string[] = [];

    historyIds.forEach((id) => {
      if (apiCommunityIds.has(id)) {
        validIds.push(id);
      } else {
        invalidIds.push(id);
      }
    });

    // 移除无效的社区ID
    if (invalidIds.length > 0) {
      invalidIds.forEach((id) => {
        communityHistoryUtils.removeHistoryCommunityId(id);
      });
      console.log(`[communityHistoryUtils] 已清理 ${invalidIds.length} 个无效的社区ID（通过API列表验证）`);
    }

    return validIds;
  }
};